import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useT } from '../contexts/LocaleContext';
import { dayEntries, slotLabelKey } from '../utils/skeletalLayout';

// Labels come from the skeleton.* strings (slots.<key>, <approvalState>).
const APPROVAL_STYLES = {
  draft:         'bg-[#F3F4F6] text-[#6B7280]',
  pending_admin: 'bg-[#FEF3C7] text-[#92400E]',
  pending_group: 'bg-[#EBF2FC] text-[#1A4A8A]',
  approved:      'bg-[#D1FAE5] text-[#065F46]',
};

function numDays(dep, ret) {
  if (!dep || !ret) return 0;
  return Math.max(1, Math.round((new Date(ret) - new Date(dep)) / 86400000) + 1);
}
function dayLabel(dep, idx, t) {
  if (!dep) return t('itinerary.dayN', { n: idx + 1 });
  const d = new Date(dep); d.setDate(d.getDate() + idx);
  return d.toLocaleDateString(t.dateLocale, { weekday: 'long', day: 'numeric', month: 'long' });
}

// Read-only skeletal itinerary (day-by-day slot overview). Used by the
// marketing and football operations portals, which can't edit it.
export default function SkeletalItineraryView({ requestId, departureDate, returnDate, showStatus = false }) {
  const [grid, setGrid]               = useState(null);
  const [hiddenCells, setHiddenCells] = useState({});
  const [dayOrder, setDayOrder]       = useState({});
  const [approvalState, setApprovalState] = useState('draft');
  const [loading, setLoading]         = useState(true);
  const t = useT();

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', requestId, 'itineraryMeta', 'skeletal'), (snap) => {
      if (snap.exists()) {
        setGrid(snap.data().grid || {});
        setHiddenCells(snap.data().hiddenCells || {});
        setDayOrder(snap.data().dayOrder || {});
        setApprovalState(snap.data().approvalState || 'draft');
      }
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [requestId]);

  if (loading) return <p className="text-[13px] text-[#9CA3AF]">{t('common.loading')}</p>;
  if (!grid || Object.keys(grid).length === 0) {
    return <p className="text-[13px] text-[#9CA3AF]">{t('skeleton.none')}</p>;
  }

  const nDays = numDays(departureDate, returnDate);
  const status = APPROVAL_STYLES[approvalState] ? approvalState : 'draft';
  return (
    <div className="space-y-5">
      {showStatus && (
        <span className={`inline-block text-[11px] font-bold px-2.5 py-1 rounded-full ${APPROVAL_STYLES[status]}`}>{t(`skeleton.${status}`)}</span>
      )}
      {Array.from({ length: nDays }, (_, d) => {
        const daySlots = dayEntries({ grid, hiddenCells, dayOrder }, d);
        if (!daySlots.length) return null;
        return (
          <div key={d}>
            <p className="text-[12px] font-bold text-[#374151] mb-2">{dayLabel(departureDate, d, t)}</p>
            <div className="space-y-1.5 pl-3 border-l-2 border-[#E5E7EB]">
              {daySlots.map(({ key, cell }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wide w-20 flex-shrink-0 ${cell.type === 'flight' ? 'text-[#16A34A]' : cell.type === 'meal' ? 'text-[#D97706]' : 'text-[#9CA3AF]'}`}>
                    {t(`skeleton.slots.${slotLabelKey(key)}`)}
                  </span>
                  <span className="text-[13px] text-[#111827]">{t.value('skeleton', cell.label)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
